import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth';
import { usePregnancyStore } from '@/store/pregnancyStore';
import type { Contraction, ContractionEmergencyEvent, EmergencyEventType } from '../types';

interface UseContractionsReturn {
  contractions: Contraction[];
  emergencyEvents: ContractionEmergencyEvent[];
  activeContraction: Contraction | null;
  isLoading: boolean;
  error: string | null;
  startContraction: () => Promise<Contraction | null>;
  stopContraction: (id: string) => Promise<boolean>;
  deleteContraction: (id: string) => Promise<boolean>;
  deleteAllContractions: () => Promise<boolean>;
  reportEmergencyEvent: (type: EmergencyEventType, notes?: string) => Promise<boolean>;
  refetch: () => Promise<void>;
}

export function useContractions(): UseContractionsReturn {
  const { user } = useAuth();
  const { currentPregnancy } = usePregnancyStore();
  const [contractions, setContractions] = useState<Contraction[]>([]);
  const [emergencyEvents, setEmergencyEvents] = useState<ContractionEmergencyEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Track if initial fetch is done
  const initialFetchDone = useRef(false);

  const userId = user?.id;
  const pregnancyId = currentPregnancy?.id;

  // Find active contraction (started but not ended)
  const activeContraction = contractions.find((c) => c.end_time === null) || null;

  // Fetch all contractions and emergency events
  const fetchData = useCallback(async () => {
    if (!pregnancyId) {
      setContractions([]);
      setEmergencyEvents([]);
      setIsLoading(false);
      return;
    }

    try {
      if (!initialFetchDone.current) {
        setIsLoading(true);
      }
      setError(null);

      // Fetch contractions and emergency events in parallel
      const [contractionsResult, eventsResult] = await Promise.all([
        supabase
          .from('contractions')
          .select('*')
          .eq('pregnancy_id', pregnancyId)
          .order('start_time', { ascending: false }),
        supabase
          .from('contraction_emergency_events')
          .select('*')
          .eq('pregnancy_id', pregnancyId)
          .order('event_time', { ascending: false }),
      ]);

      if (contractionsResult.error) throw contractionsResult.error;
      if (eventsResult.error) throw eventsResult.error;

      setContractions(contractionsResult.data || []);
      setEmergencyEvents(eventsResult.data || []);
      initialFetchDone.current = true;
    } catch (err) {
      console.error('Error fetching contractions:', err);
      setError('Fehler beim Laden der Wehen-Daten');
    } finally {
      setIsLoading(false);
    }
  }, [pregnancyId]);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Realtime subscription for contractions
  useEffect(() => {
    if (!pregnancyId) return;

    const channel = supabase
      .channel(`contractions:${pregnancyId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'contractions',
          filter: `pregnancy_id=eq.${pregnancyId}`,
        },
        () => {
          // Refetch on any change for accurate data
          fetchData();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'contraction_emergency_events',
          filter: `pregnancy_id=eq.${pregnancyId}`,
        },
        () => {
          fetchData();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [pregnancyId, fetchData]);

  // Start a new contraction
  const startContraction = useCallback(async (): Promise<Contraction | null> => {
    if (!userId || !pregnancyId) return null;

    // Check if there's already an active contraction
    if (activeContraction) {
      setError('Es läuft bereits eine Wehe');
      return null;
    }

    try {
      setError(null);

      const { data, error: insertError } = await supabase
        .from('contractions')
        .insert({
          pregnancy_id: pregnancyId,
          started_by: userId,
          start_time: new Date().toISOString(),
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Optimistic update
      setContractions((prev) => [data, ...prev]);

      return data;
    } catch (err) {
      console.error('Error starting contraction:', err);
      setError('Fehler beim Starten der Wehe');
      return null;
    }
  }, [userId, pregnancyId, activeContraction]);

  // Stop an active contraction
  const stopContraction = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        setError(null);

        const contraction = contractions.find((c) => c.id === id);
        if (!contraction) {
          setError('Wehe nicht gefunden');
          return false;
        }

        const endTime = new Date();
        const startTime = new Date(contraction.start_time);
        const durationSeconds = Math.floor((endTime.getTime() - startTime.getTime()) / 1000);

        const { error: updateError } = await supabase
          .from('contractions')
          .update({
            end_time: endTime.toISOString(),
            duration_seconds: durationSeconds,
          })
          .eq('id', id);

        if (updateError) throw updateError;

        // Optimistic update
        setContractions((prev) =>
          prev.map((c) =>
            c.id === id
              ? {
                  ...c,
                  end_time: endTime.toISOString(),
                  duration_seconds: durationSeconds,
                }
              : c
          )
        );

        return true;
      } catch (err) {
        console.error('Error stopping contraction:', err);
        setError('Fehler beim Stoppen der Wehe');
        return false;
      }
    },
    [contractions]
  );

  // Delete a contraction
  const deleteContraction = useCallback(async (id: string): Promise<boolean> => {
    try {
      setError(null);

      const { error: deleteError } = await supabase.from('contractions').delete().eq('id', id);

      if (deleteError) throw deleteError;

      // Optimistic update
      setContractions((prev) => prev.filter((c) => c.id !== id));

      return true;
    } catch (err) {
      console.error('Error deleting contraction:', err);
      setError('Fehler beim Löschen der Wehe');
      return false;
    }
  }, []);

  // Delete all contractions for this pregnancy
  const deleteAllContractions = useCallback(async (): Promise<boolean> => {
    if (!pregnancyId) return false;

    try {
      setError(null);

      const { error: deleteError } = await supabase
        .from('contractions')
        .delete()
        .eq('pregnancy_id', pregnancyId);

      if (deleteError) throw deleteError;

      // Clear local state
      setContractions([]);

      return true;
    } catch (err) {
      console.error('Error deleting all contractions:', err);
      setError('Fehler beim Löschen aller Wehen');
      return false;
    }
  }, [pregnancyId]);

  // Report an emergency event
  const reportEmergencyEvent = useCallback(
    async (type: EmergencyEventType, notes?: string): Promise<boolean> => {
      if (!userId || !pregnancyId) return false;

      try {
        setError(null);

        const { data, error: insertError } = await supabase
          .from('contraction_emergency_events')
          .insert({
            pregnancy_id: pregnancyId,
            reported_by: userId,
            event_type: type,
            event_time: new Date().toISOString(),
            notes: notes || null,
          })
          .select()
          .single();

        if (insertError) throw insertError;

        // Optimistic update
        setEmergencyEvents((prev) => [data, ...prev]);

        return true;
      } catch (err) {
        console.error('Error reporting emergency event:', err);
        setError('Fehler beim Melden des Notfall-Events');
        return false;
      }
    },
    [userId, pregnancyId]
  );

  return {
    contractions,
    emergencyEvents,
    activeContraction,
    isLoading,
    error,
    startContraction,
    stopContraction,
    deleteContraction,
    deleteAllContractions,
    reportEmergencyEvent,
    refetch: fetchData,
  };
}
